import { useEffect, useState } from "react";
import classNames from "classnames";
import withHydrationOnDemand from "react-hydration-on-demand";
import { useLinkContentActions } from "../../shared/hooks/useLinkContentAction";
import StyledButton from "../../shared/styleElements/StyledButton/StyledButton";
import {
  checkIfButtonShouldBeDisabled,
  getAddToWishlistSettings,
} from "./BoxListOfButtons.methods";
import { StyledListOfButtons } from "./BoxListOfButtons.styled";
import { IBoxListOfButtonsProps } from "./BoxListOfButtons.types";
import useIsActiveSectionButton from "@ecp-boxes/shared/hooks/useIsActiveSectionButton";
import { Memo } from "@ecp-boxes/helpers/memoWrapper";
import {
  BoxListOfButtonsContentScheme,
  BoxListOfButtonsDisplaySettingsScheme,
  BoxListOfButtonsContentSettingsScheme,
} from "./BoxListOfButtons.scheme";
import { IThemeState } from "@ecp-redux/dto/themeSettings/themeSettings.types";
import { useTheme } from "styled-components";
import { hasNativeIcon } from "@ecp-redux/helpers";
import { SelectedGoToOption } from "@ecp-boxes/settingsPatterns/contentPatterns.types";

const BoxListOfButtons: React.FC<IBoxListOfButtonsProps> = (props) => {
  const {
    globalObjects: { buttons: globalButtonsThemes },
  } = useTheme() as IThemeState;

  const {
    getHref,
    linkContentActions,
    initFirstActivePaths,
    getIsActiveButton,
    activePaths,
    isOnWishlist,
    popupRef,
  } = useLinkContentActions<HTMLButtonElement>(props.path, props.id);

  const { getIsActiveByData } = useIsActiveSectionButton(
    props.path?.getFirstSection()
  );

  const displaySettings = BoxListOfButtonsDisplaySettingsScheme.parse(
    props.displaySettings
  );

  const contentSettings = BoxListOfButtonsContentSettingsScheme.parse(
    props.contentSettings
  );

  const content = BoxListOfButtonsContentScheme.parse(props.content);

  const accountBlockedLogic = props.settings.variant_client0 === "true";

  const [isFirstRenderActiveButton, setIsFirstRenderActiveButton] =
    useState(false);

  useEffect(() => {
    if (activePaths.length === 0 && contentSettings.buttonsList.length > 0) {
      initFirstActivePaths(contentSettings.buttonsList[0]);
    }

    if (accountBlockedLogic && contentSettings.buttonsList[1]) {
      setIsFirstRenderActiveButton(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSettings.buttonsList, accountBlockedLogic]);

  useEffect(() => {
    accountBlockedLogic && linkContentActions(contentSettings.buttonsList[1]);
  }, [accountBlockedLogic]);

  const hasNativeIconCheck = hasNativeIcon(
    displaySettings.buttonsStyle,
    globalButtonsThemes
  );

  return (
    <StyledListOfButtons
      className="list-of-buttons list-of-buttons-custom-style"
      boxWidth={displaySettings.boxWidth}
      spaceBetweenButtons={displaySettings.spaceBetweenButtons}
      spaceBetweenRows={displaySettings.spaceBetweenRows}
      alignment={displaySettings.alignment}
      padding={displaySettings.padding}
      vertical={displaySettings.vertical}
    >
      <div className="list-of-buttons__container">
        {contentSettings.buttonsList.map((link, index) => {
          const { isActiveButton, isBoxSelected } = getIsActiveButton(link);
          const isIntersecting = getIsActiveByData(link);

          const href = getHref(link);

          return (
            <StyledButton
              ref={popupRef}
              {...(link
                ? {
                    href,
                    onClick: () => {
                      linkContentActions(link);
                    },
                  }
                : {})}
              target={link.goToPageSetting?.openInNewTab}
              className={classNames("list-of-buttons__container__item", {
                "active-button":
                  (accountBlockedLogic &&
                    isFirstRenderActiveButton &&
                    index === 1) ||
                  isActiveButton ||
                  isIntersecting,
                "disabled-button": checkIfButtonShouldBeDisabled(
                  isBoxSelected,
                  link,
                  href
                ),
              })}
              $settings={
                link.selectedGoToOption === SelectedGoToOption.ADD_TO_WISHLIST
                  ? getAddToWishlistSettings(
                      link.addToWishlistSetting,
                      isOnWishlist
                    ).style
                  : displaySettings.buttonsStyle
              }
              {...(!hasNativeIconCheck && {
                $uniqueIcon: contentSettings.buttonsList[index].icon,
              })}
              key={`${content.buttonsLabels[index]}${index}`}
            >
              {link.selectedGoToOption === SelectedGoToOption.ADD_TO_WISHLIST
                ? getAddToWishlistSettings(
                    link.addToWishlistSetting,
                    isOnWishlist
                  ).label
                : content.buttonsLabels[index]}
            </StyledButton>
          );
        })}
      </div>
    </StyledListOfButtons>
  );
};
export default Memo(
  withHydrationOnDemand({ on: ["visible"] })(BoxListOfButtons)
);
